/* eslint-disable */
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Row,
  Col,
  Layout,
  Modal,
  Select,
  AutoComplete,
  Spin,
  Tooltip,
  Button,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getAllProjects } from "../Actions/projectsAction";
import { useBreakpoints } from "../components/ResponsiveBreakPoints";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import "mapbox-gl/dist/mapbox-gl.css";
import no_image from "../assets/images/no_image.png";
import HomeHeader from "./Header";
import HomeFooter from "./Footer";
import DisclaimerBanner from "./DisclaimerBanner";
import "./mapListing.css";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { point } from "@turf/helpers";
import { FaMapMarkerAlt } from "react-icons/fa";
import EnquiryModal from "../pages/EnquiryModal";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const budgetOptions = [
  { label: "30 Lacs", value: 0.3 },
  { label: "40 Lacs", value: 0.4 },
  { label: "50 Lacs", value: 0.5 },
  { label: "60 Lacs", value: 0.6 },
  { label: "75 Lacs", value: 0.75 },
  { label: "90 Lacs", value: 0.9 },
  { label: "1 Cr", value: 1.0 },
  { label: "1.25 Cr", value: 1.25 },
  { label: "1.5 Cr", value: 1.5 },
  { label: "2 Cr", value: 2.0 },
  { label: "3 Cr", value: 3.0 },
  { label: "4 Cr", value: 4.0 },
  { label: "5 Cr", value: 5.0 },
  { label: "10 Cr", value: 10.0 },
  { label: "15 Cr", value: 15.0 },
];

const getBudgetLabel = (val) => {
  const option = budgetOptions.find((opt) => opt.value === val);
  return option ? option.label : "";
};

const MapListing = () => {
  const dispatch = useDispatch();
  const { isMobile } = useBreakpoints();
  const { Header } = Layout;

  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const markersRef = useRef([]);
  const drawRef = useRef(null);
  const isDraggingRef = useRef(false);
  const coordinatesRef = useRef([]);
  const isDrawingRef = useRef(false);
  const isZoomingRef = useRef(false);

  const [polygon, setPolygon] = useState(null);
  const [filteredByPolygon, setFilteredByPolygon] = useState(null);
  const [hasPolygon, setHasPolygon] = useState(false);
  const [projectType, setProjectType] = useState("Residential");
  const [searchText, setSearchText] = useState("");
  const [minBudget, setMinBudget] = useState(undefined);
  const [maxBudget, setMaxBudget] = useState(undefined);
  const [isBudgetOpen, setIsBudgetOpen] = useState(false);

  const getButtonText = () => {
    if (minBudget !== undefined && maxBudget !== undefined) {
      return `${getBudgetLabel(minBudget)} - ${getBudgetLabel(maxBudget)}`;
    }
    if (minBudget !== undefined) {
      return `${getBudgetLabel(minBudget)}+`;
    }
    if (maxBudget !== undefined) {
      return `Up to ${getBudgetLabel(maxBudget)}`;
    }
    return "Select Budget";
  };
  const [mapType, setMapType] = useState("satellite");
  const [isDrawing, setIsDrawing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [visibleProjects, setVisibleProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const { projects, isGetProjectsLoading } = useSelector(
    (state) => state.projects,
  );

  useEffect(() => {
    const handleAbortError = (event) => {
      const reason = event.reason;

      // 🔥 Ignore Mapbox tile aborts
      if (
        reason?.name === "AbortError" ||
        reason?.message?.includes("signal is aborted") ||
        reason?.toString?.().includes("AbortError")
      ) {
        event.preventDefault();
        return;
      }
    };

    window.addEventListener("unhandledrejection", handleAbortError);

    return () => {
      window.removeEventListener("unhandledrejection", handleAbortError);
    };
  }, []);

  useEffect(() => {
    isDrawingRef.current = isDrawing;
  }, [isDrawing]);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(getAllProjects(`type=${projectType}`));
  }, [projectType]);

  const filteredProjects = useMemo(() => {
    return (projects || [])
      .filter((p) => p.latitude && p.longitude)
      .filter((p) => {
        if (!searchText) return true;
        const t = searchText.toLowerCase();
        return (
          p.property_name?.toLowerCase().includes(t) ||
          p.area_name?.toLowerCase().includes(t) ||
          p.builder?.toLowerCase().includes(t)
        );
      })
      .filter((p) => {
        if (minBudget === undefined && maxBudget === undefined) return true;

        const filterMin = minBudget !== undefined ? minBudget : 0;
        const filterMax = maxBudget !== undefined ? maxBudget : Infinity;

        const price = p.price_range?.toLowerCase() || "";
        const parts = price.split(/[-–to]/);
        let propertyMin = 0;
        let propertyMax = 0;

        const parsePricePart = (part) => {
          if (!part) return 0;
          const match = part.match(/[\d.]+/);
          if (!match) return 0;
          let val = Number(match[0]);
          if (part.includes("lac") || part.includes("lakh")) {
            val = val / 100;
          }
          return val;
        };

        if (parts.length === 1) {
          const val = parsePricePart(parts[0]);
          propertyMin = val;
          propertyMax = val;
        } else if (parts.length >= 2) {
          propertyMin = parsePricePart(parts[0]);
          propertyMax = parsePricePart(parts[1]);
        }

        return propertyMax >= filterMin && propertyMin <= filterMax;
      });
  }, [projects, searchText, minBudget, maxBudget]);

  useEffect(() => {
    if (mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/satellite-v9",
      center: [72.57, 23.02],
      zoom: 11,
    });

    mapRef.current = map;

    // ✅ Better UX
    map.doubleClickZoom.disable();
    map.addControl(new mapboxgl.NavigationControl(), "top-left");

    // 🔥 shared freehand-draw handlers — bound to BOTH mouse + touch
    // so the boundary draw works on desktop AND mobile/touch devices
    const handleDrawStart = (e) => {
      if (!isDrawingRef.current) return;

      // 🔥 ignore multi-finger gestures so pinch-zoom keeps working
      if (e.points && e.points.length > 1) return;

      // 🔥 on touch: stop the map panning / page scrolling while drawing
      if (e.type && e.type.indexOf("touch") === 0 && e.preventDefault) {
        e.preventDefault();
      }

      isDraggingRef.current = true;
      coordinatesRef.current = [[e.lngLat.lng, e.lngLat.lat]];

      map.dragPan.disable(); // 🔥 stop map moving
    };

    const handleDrawMove = (e) => {
      if (!isDraggingRef.current) return;

      // 🔥 on touch: stop page scrolling while drawing
      if (e.type && e.type.indexOf("touch") === 0 && e.preventDefault) {
        e.preventDefault();
      }

      coordinatesRef.current.push([e.lngLat.lng, e.lngLat.lat]);

      // 🔥 lightweight preview (NO React state)
      const poly = {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [[...coordinatesRef.current, coordinatesRef.current[0]]],
        },
      };

      const source = map.getSource("draw-polygon");
      if (source) {
        source.setData({
          type: "FeatureCollection",
          features: [poly],
        });
      }
    };

    const handleDrawEnd = () => {
      if (!isDraggingRef.current) return;

      isDraggingRef.current = false;

      // 🔥 a stray tap (no drag) shouldn't wipe the list — need a real shape
      if (coordinatesRef.current.length < 3) {
        map.dragPan.enable();
        map.getCanvas().style.cursor = "";
        return;
      }

      const poly = {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [[...coordinatesRef.current, coordinatesRef.current[0]]],
        },
      };

      // 🔥 React update ONLY once
      setPolygon(poly);
      setHasPolygon(true);
      setIsDrawing(false);

      // 🔥 draw on map
      const source = map.getSource("draw-polygon");
      if (source) {
        source.setData({
          type: "FeatureCollection",
          features: [poly],
        });
      }

      map.dragPan.enable();
      map.getCanvas().style.cursor = "";
    };

    // 🖱️ desktop
    map.on("mousedown", handleDrawStart);
    map.on("mousemove", handleDrawMove);
    map.on("mouseup", handleDrawEnd);

    // 📱 mobile / touch
    map.on("touchstart", handleDrawStart);
    map.on("touchmove", handleDrawMove);
    map.on("touchend", handleDrawEnd);

    map.on("load", () => {
      map.addSource("draw-polygon", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.addLayer({
        id: "draw-polygon-fill",
        type: "fill",
        source: "draw-polygon",
        paint: {
          "fill-color": "#3b82f6",
          "fill-opacity": 0.25,
        },
      });

      map.addLayer({
        id: "draw-polygon-line",
        type: "line",
        source: "draw-polygon",
        paint: {
          "line-color": "#2563eb",
          "line-width": 3,
        },
      });
    });
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    const style =
      mapType === "satellite"
        ? "mapbox://styles/mapbox/satellite-v9"
        : mapType === "hybrid"
          ? "mapbox://styles/mapbox/satellite-streets-v11"
          : "mapbox://styles/mapbox/streets-v11";

    map.setStyle(style);

    // 🔥 IMPORTANT: re-add drawing after style loads
    map.once("style.load", () => {
      if (!map.getSource("draw-polygon")) {
        map.addSource("draw-polygon", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [],
          },
        });

        map.addLayer({
          id: "draw-polygon-fill",
          type: "fill",
          source: "draw-polygon",
          paint: {
            "fill-color": "#3b82f6",
            "fill-opacity": 0.25,
          },
        });

        map.addLayer({
          id: "draw-polygon-line",
          type: "line",
          source: "draw-polygon",
          paint: {
            "line-color": "#2563eb",
            "line-width": 3,
          },
        });
      }
    });
  }, [mapType]);

  useEffect(() => {
    // 🔥 no polygon → no results
    if (!polygon || !polygon.geometry || !polygon.geometry.coordinates) {
      setFilteredByPolygon([]);
      return;
    }

    const result = filteredProjects.filter((p) => {
      if (!p.latitude || !p.longitude) return false;

      try {
        const pt = point([Number(p.longitude), Number(p.latitude)]);
        return booleanPointInPolygon(pt, polygon);
      } catch (err) {
        return false; // 🔥 prevent crash
      }
    });

    setFilteredByPolygon(result);
  }, [polygon, filteredProjects]);

  const displayProjects = isDrawing
    ? filteredByPolygon || []
    : hasPolygon
      ? filteredByPolygon || []
      : filteredProjects;

  useEffect(() => {
    if (!mapRef.current) return;

    // remove old markers
    markersRef.current.forEach((m) => m.marker.remove());
    markersRef.current = [];

    displayProjects.forEach((p) => {
      // popup
      const popup = new mapboxgl.Popup({
        offset: [0, -35],
        closeButton: true,
      }).setHTML(`
          <div class="map-card">
            
            <div class="map-card-image" style="background-image:url('${
              p?.images?.[0]?.image_url?.url || no_image
            }')"></div>
            
            <div class="map-card-body">
              <div class="map-card-title">${p.property_name}</div>
              <div class="map-card-builder">by ${p.builder}</div>
    
              <div class="map-card-location">
                ${p.area_name}
              </div>
    
              <div class="map-card-footer">
                <span>${p.bhk}</span>
                <span>₹${p.price_range}</span>
              </div>
    
            </div>
          </div>
        `);

      // marker
      const marker = new mapboxgl.Marker({
        color: "#1c3988",
      })
        .setLngLat([Number(p.longitude), Number(p.latitude)])
        .setPopup(popup)
        .addTo(mapRef.current);

      // popup open
      popup.on("open", () => {
        setSelectedProjectId(p.id);

        // reset all markers
        markersRef.current.forEach((m) => {
          const markerSvg = m.marker.getElement().querySelector("svg");

          if (markerSvg) {
            const paths = markerSvg.querySelectorAll("path");

            if (paths[0]) {
              paths[0].setAttribute("fill", "#1c3988");
            }
          }
        });

        // current marker red
        const svg = marker.getElement().querySelector("svg");

        if (svg) {
          const paths = svg.querySelectorAll("path");

          if (paths[0]) {
            paths[0].setAttribute("fill", "#ef4444");
          }
        }
      });

      // popup close
      popup.on("close", () => {
        const svg = marker.getElement().querySelector("svg");

        if (svg) {
          const paths = svg.querySelectorAll("path");

          if (paths[0]) {
            paths[0].setAttribute("fill", "#1c3988");
          }
        }

        setSelectedProjectId((current) => (current === p.id ? null : current));
      });

      // store marker
      markersRef.current.push({
        id: p.id,
        marker,
      });
    });
  }, [displayProjects]);

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    const updateVisibleProjects = () => {
      const bounds = map.getBounds();

      const visible = displayProjects.filter((p) => {
        if (!p.latitude || !p.longitude) return false;

        return bounds.contains([Number(p.longitude), Number(p.latitude)]);
      });

      setVisibleProjects(visible);
    };

    // initial
    updateVisibleProjects();

    // update on move/zoom
    map.on("moveend", updateVisibleProjects);

    return () => {
      map.off("moveend", updateVisibleProjects);
    };
  }, [displayProjects]);

  const handleDraw = () => {
    setIsDrawing(true);
    coordinatesRef.current = [];

    if (mapRef.current) {
      mapRef.current.getCanvas().style.cursor = "crosshair";
    }
  };

  const handleCancelDraw = () => {
    setIsDrawing(false);
    setPolygon(null);
    setFilteredByPolygon(null);
    setHasPolygon(false);

    coordinatesRef.current = [];

    if (mapRef.current) {
      const source = mapRef.current.getSource("draw-polygon");
      if (source) {
        source.setData({
          type: "FeatureCollection",
          features: [],
        });
      }

      mapRef.current.dragPan.enable();
      mapRef.current.getCanvas().style.cursor = "";
    }
  };

  const handleDelete = () => {
    setPolygon(null);
    setFilteredByPolygon(null);
    setHasPolygon(false);
    setIsDrawing(false);

    coordinatesRef.current = [];

    // 🔥 clear map drawing
    if (mapRef.current) {
      const source = mapRef.current.getSource("draw-polygon");
      if (source) {
        source.setData({
          type: "FeatureCollection",
          features: [],
        });
      }
    }
  };

  const handleOpenDetailsModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleOpenEnquiryModal = (project) => {
    setSelectedProject(project);
    setIsEnquiryModalOpen(true);
  };

  const handleSelectProject = (project) => {
    if (!mapRef.current || isZoomingRef.current || !project) return;

    isZoomingRef.current = true;

    setSearchText(project.property_name);

    const lng = Number(project.longitude);
    const lat = Number(project.latitude);

    mapRef.current.easeTo({
      center: [lng, lat],
      zoom: 14,
      duration: 1500,
    });

    mapRef.current.once("moveend", () => {
      isZoomingRef.current = false;
    });
  };

  const handleZoomToSearch = (term, option) => {
    if (!mapRef.current || !term) return;

    if (option?.project) {
      handleSelectProject(option.project);
      return;
    }

    const t = (option?.areaName || term).toLowerCase().trim();
    if (!t) return;

    const matching = (projects || []).filter(
      (p) =>
        p.latitude &&
        p.longitude &&
        (p.area_name?.toLowerCase().includes(t) ||
          p.property_name?.toLowerCase().includes(t) ||
          p.builder?.toLowerCase().includes(t))
    );

    if (matching.length === 1) {
      mapRef.current.easeTo({
        center: [Number(matching[0].longitude), Number(matching[0].latitude)],
        zoom: 14,
        duration: 1500,
      });
    } else if (matching.length > 1) {
      const bounds = new mapboxgl.LngLatBounds();
      matching.forEach((p) => {
        bounds.extend([Number(p.longitude), Number(p.latitude)]);
      });
      mapRef.current.fitBounds(bounds, {
        padding: 80,
        maxZoom: 15,
        duration: 1500,
      });
    } else {
      if (mapboxgl.accessToken) {
        fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            term
          )}.json?access_token=${mapboxgl.accessToken}`
        )
          .then((res) => res.json())
          .then((data) => {
            if (data.features && data.features.length > 0 && mapRef.current) {
              const [lng, lat] = data.features[0].center;
              mapRef.current.flyTo({
                center: [lng, lat],
                zoom: 13,
                duration: 1500,
              });
            }
          })
          .catch(() => {});
      }
    }
  };

  const searchOptions = useMemo(() => {
    if (!searchText) return [];
    const t = searchText.toLowerCase().trim();

    const matchingAreas = Array.from(
      new Set(
        (projects || [])
          .map((p) => p.area_name)
          .filter((area) => area && area.toLowerCase().includes(t))
      )
    ).slice(0, 3);

    const areaOptions = matchingAreas.map((area) => ({
      value: area,
      label: (
        <div>
          <div
            style={{
              fontSize: "14px",
              fontFamily: "Roboto-500",
              color: "#1c3988",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <FaMapMarkerAlt size={12} />
            {area}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "#888",
              fontFamily: "Roboto-400",
            }}
          >
            Location / Area
          </div>
        </div>
      ),
      areaName: area,
      isArea: true,
    }));

    const matchingProjects = (displayProjects || [])
      .filter(
        (p) =>
          p.property_name?.toLowerCase().includes(t) ||
          p.builder?.toLowerCase().includes(t)
      )
      .slice(0, 5);

    const projectOptions = matchingProjects.map((p) => ({
      value: p.property_name,
      label: (
        <div>
          <div
            style={{
              fontSize: "14px",
              fontFamily: "Roboto-500",
            }}
          >
            {p.property_name}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#888",
              fontFamily: "Roboto-400",
            }}
          >
            {p.area_name} {p.builder ? `• by ${p.builder}` : ""}
          </div>
        </div>
      ),
      project: p,
      isArea: false,
    }));

    return [...areaOptions, ...projectOptions];
  }, [projects, displayProjects, searchText]);

  return (
    <>
      <Header className="header">
        <HomeHeader />
      </Header>
      <Row justify="center">
        <Col span={24}>
          <div className="container">
            {/* FILTER */}
            <Row justify="center">
              <Col xs={22} sm={22} md={22} lg={18} xl={20} xxl={16}>
                <div className="filters">
                  {/* Property Type */}
                  <Select
                    value={projectType}
                    onChange={(value) => setProjectType(value)}
                    className="filter-select"
                  >
                    <Select.Option value="Residential">
                      Residential
                    </Select.Option>
                    <Select.Option value="Commercial">Commercial</Select.Option>
                    <Select.Option value="Land">Land</Select.Option>
                  </Select>

                  {/* All Cities */}
                  {/* <Select defaultValue="All Cities" className="filter-select">
                <Select.Option value="all">All Cities</Select.Option>
                <Select.Option value="Ahmedabad">Ahmedabad</Select.Option>
              </Select> */}

                  {/* 🔥 Search */}
                  <AutoComplete
                    value={searchText}
                    allowClear
                    onChange={(value) => {
                      setSearchText(value || "");
                      if (value && value.trim().length >= 2) {
                        handleZoomToSearch(value);
                      }
                    }}
                    onSelect={(value, option) => {
                      setSearchText(value);
                      handleZoomToSearch(value, option);
                    }}
                    className="search-input"
                    placeholder="Search By Area, Project or Builder"
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    options={searchOptions}
                  />

                  {/* ✅ Budget */}
                  <Select
                    value={
                      minBudget === undefined && maxBudget === undefined
                        ? undefined
                        : getButtonText()
                    }
                    placeholder="Select Budget"
                    className="filter-select"
                    allowClear
                    onChange={(value) => {
                      if (!value) {
                        setMinBudget(undefined);
                        setMaxBudget(undefined);
                      }
                    }}
                    open={isBudgetOpen}
                    onDropdownVisibleChange={(visible) =>
                      setIsBudgetOpen(visible)
                    }
                    dropdownStyle={{ padding: 0 }}
                    dropdownClassName="budget-select-dropdown"
                    dropdownRender={() => (
                      <div
                        className="budget-dropdown-container"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      >
                        <div className="budget-dropdown-row">
                          <Select
                            placeholder="Min"
                            value={minBudget}
                            onChange={(value) => setMinBudget(value)}
                            allowClear
                            getPopupContainer={(triggerNode) =>
                              triggerNode.parentNode
                            }
                          >
                            {budgetOptions.map((opt) => (
                              <Select.Option
                                key={opt.value}
                                value={opt.value}
                                disabled={
                                  maxBudget !== undefined &&
                                  opt.value > maxBudget
                                }
                              >
                                {opt.label}
                              </Select.Option>
                            ))}
                          </Select>
                          <span className="dash">–</span>
                          <Select
                            placeholder="Max"
                            value={maxBudget}
                            onChange={(value) => setMaxBudget(value)}
                            allowClear
                            getPopupContainer={(triggerNode) =>
                              triggerNode.parentNode
                            }
                          >
                            {budgetOptions.map((opt) => (
                              <Select.Option
                                key={opt.value}
                                value={opt.value}
                                disabled={
                                  minBudget !== undefined &&
                                  opt.value < minBudget
                                }
                              >
                                {opt.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            type="button"
                            className="budget-clear-btn"
                            onClick={() => {
                              setMinBudget(undefined);
                              setMaxBudget(undefined);
                            }}
                          >
                            Clear All
                          </button>
                        </div>
                      </div>
                    )}
                  />
                </div>
              </Col>
            </Row>

            {/* MAIN */}
            <div className={`main ${isMobile ? "column" : "row"}`}>
              {/* MAP */}
              <div className="map-wrapper">
                <div ref={mapContainerRef} className="map" />

                <div className="map-controls">
                  {/* Toggle */}
                  <div className="map-toggle">
                    {["map", "satellite", "hybrid"].map((t) => (
                      <button
                        key={t}
                        onClick={() => setMapType(t)}
                        className={`map-btn ${mapType === t ? "active" : ""}`}
                      >
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </button>
                    ))}
                  </div>

                  {/* DRAW MODE */}
                  {!isDrawing && !hasPolygon && (
                    <button className="draw-btn" onClick={handleDraw}>
                      ✏️ Draw Boundary
                    </button>
                  )}

                  {/* DRAWING ACTIVE */}
                  {isDrawing && (
                    <div className="draw-active">
                      ✏️ Draw Mode Active
                      <span onClick={handleCancelDraw}>✖</span>
                    </div>
                  )}

                  {/* CLEAR */}
                  {hasPolygon && !isDrawing && (
                    <button className="draw-btn delete" onClick={handleDelete}>
                      🗑️ Clear All
                    </button>
                  )}
                </div>
              </div>

              {/* LIST */}

              <div className="list">
                <Spin spinning={isGetProjectsLoading}>
                  <div className="result-header">
                    <span className="count">
                      {visibleProjects.length} Results
                    </span>
                    <span className="divider">|</span>
                    <span className="type">{projectType} in Ahmedabad</span>
                  </div>
                  <Row gutter={[12, 12]} className="mobile-scroll-row">
                    {visibleProjects.map((p) => (
                      <Col
                        xs={24}
                        sm={12}
                        md={24}
                        xl={12}
                        key={p.id}
                        className="mobile-card-col"
                      >
                        <PropertyCard
                          project={p}
                          isSelected={selectedProjectId === p.id}
                          onCardClick={() => handleOpenDetailsModal(p)}
                          onButtonClick={() => handleOpenEnquiryModal(p)}
                        />
                      </Col>
                    ))}
                  </Row>
                </Spin>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <HomeFooter />
      <DisclaimerBanner />
      <EnquiryModal
        open={isEnquiryModalOpen}
        onCancel={() => setIsEnquiryModalOpen(false)}
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
        setIsEnquiryModalOpen={setIsEnquiryModalOpen}
      />
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={600}
        closeIcon={false}
      >
        {selectedProject && (
          <div className="property-modal">
            <div className="modal-close" onClick={() => setIsModalOpen(false)}>
              ✕
            </div>
            <div
              className="modal-image"
              style={{
                backgroundImage: `url(${
                  selectedProject?.images?.[0]?.image_url?.url || no_image
                })`,
              }}
            />

            <div className="modal-content">
              <div className="property-title">
                {selectedProject.property_name}
              </div>
              <p className="builder">by {selectedProject.builder}</p>

              <div className="modal-grid">
                <div>Location: {selectedProject.area_name}</div>
                <div>BHK: {selectedProject.bhk}</div>
                <div>Price: ₹{selectedProject.price_range}</div>
                <div>Possession: {selectedProject.possesion}</div>
                <div>Total Units: {selectedProject.total_units}</div>
                {projectType !== "Land" && (
                  <div>Floors: {selectedProject.no_of_floor}</div>
                )}
              </div>

              {/* description */}
              {(() => {
                const descList = Array.isArray(selectedProject.description)
                  ? selectedProject.description
                  : selectedProject.description?.description;
                if (!Array.isArray(descList) || descList.length === 0)
                  return null;
                return (
                  <div className="description">
                    <div className="description-title">Description</div>
                    <div className="description-grid">
                      {descList.map((d, i) => (
                        <div key={i} className="description-card">
                          {d.key ? (
                            <span>
                              <span className="desc-key">{d.key}:</span>{" "}
                              <span className="desc-value">{d.value}</span>
                            </span>
                          ) : (
                            <span className="desc-value">{d.value}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {selectedProject.tag_line && (
                <div className="tagline">{selectedProject.tag_line}</div>
              )}
            </div>
          </div>
        )}
      </Modal>
      ;
    </>
  );
};

const PropertyCard = ({ project, onCardClick, onButtonClick, isSelected }) => {
  const image = project?.images?.[0]?.image_url?.url || no_image;

  return (
    <div
      className={`card clickable-card ${
        isSelected ? "active-property-card" : ""
      }`}
      onClick={onCardClick}
    >
      <div
        className="card-image"
        style={{ backgroundImage: `url(${image})` }}
      />

      <div className="card-body">
        <div className="card-header">
          <div className="card-title">{project.property_name}</div>

          <Tooltip
            title={
              project?.project_pdf?.file?.url
                ? "Download Brochure"
                : "Get Offer"
            }
          >
            <button
              className="card-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                onButtonClick();
              }}
            >
              {project?.project_pdf?.file?.url ? "Brochure" : "Get Offer"}
            </button>
          </Tooltip>
        </div>
        <div className="card-builder">by {project.builder}</div>

        <div className="card-divider" />

        <div className="card-location">
          <FaMapMarkerAlt size={10} /> {project.area_name}
        </div>

        <div className="card-footer">
          <span className="priceProperty">{project.bhk}</span>
          <span className="premium-price-wrapper">
            <span className="premium-price">₹{project.price_range}</span>
            <Tooltip
              title={<span className="premium-tooltip-text">Basic Price only</span>}
              trigger="hover"
              placement="top"
              overlayClassName="premium-price-tooltip"
            >
              <InfoCircleOutlined
                className="premium-price-info-icon"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            </Tooltip>
          </span>
        </div>
      </div>
    </div>
  );
};

export default MapListing;
